import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router";
import constants from '../../../constants/constants';
import { Button, Card, Checkbox, Col, Divider, Form, message, Popconfirm, Radio, Row, Select, Space, Spin, Tag } from 'antd';
import { DOB } from '../../../globalFunctions/GlobalFunctions';
import { fetchAdminApi } from '../../../services/api';
import { COOKIE, getCookie } from '../../../services/cookie';
import moment from 'moment';
import RecoveryPlanSummary from './RecoveryPlanSummary';

const { Option } = Select;

const RecoveryPlan = () => {
	const history = useHistory();
	const [form] = Form.useForm();
	const adminClientValue = JSON.parse(sessionStorage.getItem("adminClientValue"));
	const [selectedModalities, setSelectedModalities] = useState({});
	const [selectedGoalStatement, setSelectedGoalStatement] = useState({});
	const [selectedAreaComprehensiveEvaluation, setSelectedAreaComprehensiveEvaluation] = useState({});
	const [loader, setLoader] = useState(false);
	const [selectedSummarySections, setSelectedSummarySections] = useState([]);
	const [noteId, setNoteId] = useState(null);
	const [isSigned, setIsSigned] = useState(false);
	const [noteData, setNoteData] = useState(false);
	const isAdmin = getCookie(COOKIE.LoginType) === "0" ? false : true;

	const current = new Date();
	const [date, setDate] = useState({
		date: current.getDate(),
		month: (current.getMonth() + 1).toString().padStart(2, '0'),
		year: current.getFullYear()
	});
	const handleDayChange = (value) => {
		setDate({
			...date,
			date: value
		});
	};
	const handleMonthChange = (value) => {
		setDate({
			...date,
			month: value
		});
	};
	const handleYearChange = (value) => {
		setDate({
			...date,
			year: value
		});
	};

	useEffect(() => {
		getRecoveryPlanAnswer();
	}, []);
	
	const getRecoveryPlanAnswer = () => {
		let clientId = isAdmin ? adminClientValue?.clientId : getCookie(COOKIE.ClientId);
		setLoader(true);
		fetchAdminApi(`/get-recoveryplan-answer/${clientId}`, 'get').then((res) => {
			if (res?.code && res.code === 200) {
				setNoteData(res?.data);
				let data = null;
				if (res?.data?.screening_data?.screening) {
					data = res?.data?.screening_data?.screening
				} else {
					data = res?.data?.recovery_plan_data
				}
				form.setFieldsValue(data);
				setSummaryData(data);
				if (res?.data?.notes_id) {
					setNoteId(res?.data?.notes_id);
				} else {
					setNoteId(null);
				}
				if (res?.data?.signed_by) {
					setIsSigned(true); // disable the form if signed
				}
				if (res?.data?.date_of_notes) {
					const noteDateObj = new Date(res?.data?.date_of_notes);
					setDate({
						date: noteDateObj.getDate().toString().padStart(2, '0'),
						month: (noteDateObj.getMonth() + 1).toString().padStart(2, '0'),
						year: noteDateObj.getFullYear().toString()
					});
				}
			} else {
				setNoteData(null);
				setNoteId(null);
				setIsSigned(false);
				message.error(res?.message);
			}
		})
			.catch((err) => {
				setNoteData(null);
				setNoteId(null);
				setIsSigned(false);
				message.error("Failed to load recovery plan data.");
				console.error(err);
			})
			.finally(() => {
				setLoader(false);
			});
	};

	const setSummaryData = (resdata) => {
		setSelectedSummarySections(resdata?.selected_summary_sections);
		const selectedAreaComprehensiveEvaluation = {};
		const selectedGoalStatement = {};
		const selectedIntervention= {};
		
		constants.ComprehensivePsychosocialOptions.forEach(option => {
			const areaField = option?.questions[0]?.name; // e.g., 'Chemical_Problem'
			const goalField = option?.questions[1]?.name; // e.g., 'Chemical_Goal'
			const interventionField = option?.key + "interventionmodality"
		
			const areaData = resdata[areaField] || [];
			const goalData = resdata[goalField] || [];
			const interventionData = resdata[interventionField] || [];
		
			// Convert areaData to objects with label
			const areaMapped = areaData
				.map(item => {
					const found = option?.questions[0]?.options?.find(opt => opt.key === item);
					if (found) return { key: found.key, label: found.label };
					return typeof item === 'string' ? { key: item, label: item } : null;
				})
				.filter(Boolean);
		
			// Convert goalData to objects with label
			const goalMapped = goalData
				.map(item => {
					const found = option?.questions[1]?.options?.find(opt => opt.label === item || opt.key === item);
					if (found) return { key: found.key, label: found.label };
					return typeof item === 'string' ? { key: item, label: item } : null;
				})
				.filter(Boolean);
			// Convert goalData to objects with label
			const interventionMapped = interventionData
				.map(item => {
					const found = option?.interventionmodalityList?.find(opt => opt.label === item || opt.value === item);
					if (found) return found.value;
					return typeof item === 'string' ? { value: item, label: item } : null;
				})
				.filter(Boolean);
			if (areaMapped.length) selectedAreaComprehensiveEvaluation[option.key] = areaMapped;
			if (goalMapped.length) selectedGoalStatement[option.key] = goalMapped;
			if (interventionMapped.length) selectedIntervention[option.key] = interventionMapped;
		});
		setSelectedModalities(selectedIntervention);
		setSelectedAreaComprehensiveEvaluation(selectedAreaComprehensiveEvaluation);
		setSelectedGoalStatement(selectedGoalStatement);
	};

	const tagRender = (props) => {
		const { label, closable, onClose } = props;

		return (
			<Tag
				closable={!isSigned && closable}
				onClose={onClose}
				style={{
					whiteSpace: 'normal',
					overflowWrap: 'break-word',
					wordBreak: 'break-word',
					maxWidth: '100%',
					lineHeight: '1.2',
				}}
				className={`!h-auto !inline-flex !items-center !break-words !whitespace-normal !text-sm ${isSigned ? 'ant-select-disabled' : ''}`}
			>
				{label}
			</Tag>
		);
	};

	const renderSection = (sectiondata) => {
		return <Row gutter={[24, 0]}>
			<Col span={24}><div className='mb-2 font-semibold italic text-lg underline'><Checkbox onChange={() => onChangeSummarySection(sectiondata?.key)} checked={selectedSummarySections?.length > 0 ? selectedSummarySections?.includes(sectiondata?.key) : false} disabled={isSigned}><span>{sectiondata?.topic}</span></Checkbox></div></Col>
			<Col span={24}>
				<Form.Item
					name={sectiondata?.key}
				>
					<Radio.Group disabled={isSigned}>
						{constants.IndividualRecoveryPlanType.map(({ value, label }) => (
							<Radio key={value} value={value}>{label}</Radio>
						))}
					</Radio.Group>
				</Form.Item>
			</Col>
			<Col span={24}>
				<Form.Item
					label={constants.RecoveryPlanLabel1}
					name={sectiondata?.questions[0]?.name}
					className='mb-0'
				>
					<Select
						mode="tags"
						disabled={isSigned}
						placeholder={sectiondata?.questions[0]?.placeholder}
						allowClear
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							option?.children?.toLowerCase().includes(input.toLowerCase())
						}
						onChange={(a, b) => onChangeAreaComprehensiveEvaluation(a, b, sectiondata?.key, sectiondata?.questions?.[0].options)}
						tagRender={tagRender}
					>
						{sectiondata?.questions?.[0].options?.map((option) => (
							<Option key={option.key} value={option.label}><div className='whitespace-normal'>{option.label}</div></Option>
						))}
					</Select>
				</Form.Item>
			</Col>
			<Col span={24}>
				<Form.Item
					label={constants.RecoveryPlanLabel2}
					name={sectiondata?.questions[1]?.name}
					className='mb-0'
				>
					<Select
						mode="tags"
						disabled={isSigned}
						placeholder={sectiondata?.questions[1]?.placeholder}
						allowClear
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							option?.children?.toLowerCase().includes(input.toLowerCase())
						}
						onChange={(a, b) => onChangeGoalStatement(a, b, sectiondata?.key, sectiondata?.questions?.[1].options)}
						tagRender={tagRender}
					>
						{sectiondata?.questions?.[1].options?.map((option) => (
							<Option key={option.key} value={option.label}><div className='whitespace-normal'>{option.label}</div></Option>
						))}
					</Select>
				</Form.Item>
			</Col>
			<Col xs={24} lg={24}>
				<Form.Item
					label={constants.Objectives}
					name={sectiondata?.key + "objectives"}
				>
					<Select
						disabled={isSigned}
						placeholder={constants.Objectives}
						allowClear
						mode='tags'
						showSearch
						filterOption={(input, option) =>
							option?.children?.toLowerCase().includes(input.toLowerCase())
						}
						tagRender={tagRender}
					>
						{sectiondata?.objectivesList.map(({ value, label }) => (
							<Option key={value} value={label}><div className='whitespace-normal'>{label}</div></Option>
						))}
					</Select>
				</Form.Item>
			</Col>
			<Col xs={24} lg={24}>
				<Form.Item
					label={constants.InterventionModality}
					name={sectiondata?.key + "interventionmodality"}
				>
					<Select
						disabled={isSigned}
						placeholder={constants.InterventionModality}
						allowClear
						mode='multiple'
						showSearch
						filterOption={(input, option) =>
							option?.children?.toLowerCase().includes(input.toLowerCase())
						}
						onChange={(a, b) => onChangeInterventionModality(a, b, sectiondata?.key)}
						tagRender={tagRender}
					>
						{sectiondata?.interventionmodalityList?.map(({ value, label }) => (
							<Option key={value} value={value}><div className='whitespace-normal'>{label}</div></Option>
						))}
					</Select>
				</Form.Item>
			</Col>
			{groupDropdown(sectiondata?.key, sectiondata?.interventionmodalityList)}
		</Row>
	};

	const groupDropdown = (type, interventionmodalityList) => {
		const filtered = interventionmodalityList.filter(item => selectedModalities[type]?.includes(item.value));
		const hasGroupSession = filtered?.some(item => item.label === "Group session.");
		if (hasGroupSession) {
			return (
				<Col xs={24} lg={12}>
					<Form.Item
						label={constants.GroupSession}
						name={type + "_group_session"}
					>
						<Select
							disabled={isSigned}
							placeholder={constants.GroupSession}
							allowClear
							mode='multiple'
							showSearch
							filterOption={(input, option) =>
								option?.children?.toLowerCase()?.includes(input.toLowerCase())
							}
							tagRender={tagRender}
						>
							{constants.GroupSessionList.map(({ value, label }) => (
								<Option key={value} value={value}><div className='whitespace-normal'>{label}</div></Option>
							))}
						</Select>
					</Form.Item>
				</Col>
			)
		} else {
			return null
		}
	};

	const onChangeInterventionModality = (selectedValues, optionList, type) => {
		setSelectedModalities(prev => ({
			...prev,
			[type]: selectedValues,
		}));
	};

	const onChangeGoalStatement = (selectedValues, _optionList, type, chemicalDependenceArray) => {
		const filtered = chemicalDependenceArray?.filter(item => selectedValues?.includes(item.label));
		setSelectedGoalStatement(prev => ({
			...prev,
			[type]: filtered,
		}));
	};

	const onChangeAreaComprehensiveEvaluation = (selectedValues, _optionList, type, chemicalDependenceArray) => {
		const filtered = chemicalDependenceArray?.filter(item => selectedValues?.includes(item.label));
		setSelectedAreaComprehensiveEvaluation(prev => ({
			...prev,
			[type]: filtered,
		}));
	};

	const handleEsignConfirm = () => {
		form
			.validateFields()
			.then((values) => {
				onFinishForm(values, "esign");
			})
			.catch(() => {
				message.error("Please try again later");
			});
	};	

	const handleBackClick = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			handleRedirect();
		}
	};

	const handleRedirect = () => {
		if (!isAdmin) {
			history.push("/dashboard");
		} else {
			if (adminClientValue?.clientId) {
				history.push("/view-client");
			} else {
				history.push("/view-enquiry");
			}
		}
	};

	const onFinishForm = (values, type = null) => {
		setLoader(true);
		let admin_id = getCookie(COOKIE.ClientId);
		let formdata = values;
		formdata.selected_summary_sections = selectedSummarySections;
		formdata.selected_goal_statement = selectedGoalStatement;
		formdata.selected_evaluation = selectedAreaComprehensiveEvaluation;
		let payload = {
			notes_content: null,
			notes_assesment: null,
			notes_plan: null,
			section_id: null,
			notes_type: "6",
			dt_created: `${date.year}-${date.month}-${date?.date}`,
			answers: formdata,
			admin_id: admin_id
		};

		if (type === "esign") {
			payload.signed_by = admin_id;
			const currentDate = new Date();
			payload.dt_updated = currentDate.toISOString().split("T")[0];
		}

		if (adminClientValue?.clientId) {
			payload.client_id = adminClientValue?.clientId;
		} else {
			payload.enquiry_id = adminClientValue?.enquiryId;
		}
		let method;
		let url;
		if(noteId !== null){
			payload.notes_id = noteId;
			method = "put";
			url = '/edit-notes';
		}else{
			method = "post";
			url = '/add-notes';
		}
		fetchAdminApi(url, method, payload).then((res) => {
			if (res?.code && res.code === 200) {
				setLoader(false);
				message.success(res?.message);
				if (adminClientValue?.clientId) {
					history.push("/view-client");
				} else {
					history.push("/view-enquiry");
				}
			} else {
				setLoader(false);
				message.error(res?.message);
			}
		});
	};

	const onChangeSummarySection = (key) => {
		setSelectedSummarySections((prev = []) => {
			const isSelected = prev.includes(key);
			const updated = isSelected
				? prev.filter(k => k !== key)
				: [...prev, key];
			return updated;
		});
	};
	
	return (
		<div className='add-note'>
			<div className='text-center bg-amber-300 text-lg font-semibold p-3'>
				{constants.Subtitle}
				<span className="py-0.5 px-3 ml-3 text-sm bg-white rounded-xl">
					{constants.IndividualRecoveryPlan}
				</span>
			</div>
			<div className='bg-amber-100 text-base font-semibold p-2 mb-3'>
				<div className='container mx-auto'>
					<Space size={'small'} wrap direction="vertical">
						{isAdmin ? `${constants.IndividualRecoveryPlan} : ${adminClientValue?.client_name}${adminClientValue?.client_serial_number ? `(# ${adminClientValue?.client_serial_number})` : null}` : `My ${constants.IndividualRecoveryPlan}`}
						{isAdmin && <Button size='small' className='text-xs font-semibold bg-sky-800 text-white uppercase' onClick={handleBackClick}>{constants.BackBtn}</Button>}
					</Space>
				</div></div>
			<div className='container mx-auto'>
				<div className="px-2 pb-2">
					{DOB(date.month, handleMonthChange, date.date, handleDayChange, date.year, handleYearChange, "Date", isSigned)}
				<Spin spinning={loader} size="large">
					<Card 
					>
							<Form layout='vertical' className='mb-10 text-black client-details disabled-dark' onFinish={onFinishForm} form={form}>
							{constants.ComprehensivePsychosocialOptions.map((section, index) => (
								<React.Fragment key={index}>
									{renderSection(section)}
									<Divider />
								</React.Fragment>
							))}
							<RecoveryPlanSummary
								selectedSummarySections={selectedSummarySections}
								selectedGoalStatement={selectedGoalStatement}
								selectedAreaComprehensiveEvaluation={selectedAreaComprehensiveEvaluation}
								options={constants.ComprehensivePsychosocialOptions}
							/>
							<Form.Item>
								<Divider className='my-7' />
									<Space wrap>
									<Button className='bg-sky-800 text-white uppercase' type='primary' onClick={handleRedirect}>
										{constants.Cancel}
									</Button>
										{
											isAdmin &&
											(
												<>
													<Button className='bg-sky-800 text-white uppercase' type='primary' htmlType="submit" disabled={isSigned}>
														{constants.Save}
													</Button>
													<Popconfirm title="You will not be able to Edit after you Esign the Notes" onConfirm={handleEsignConfirm}>
														<Button className='bg-sky-800 text-white uppercase' disabled={isSigned}>{constants.Esign}</Button>
													</Popconfirm>
													{
														isSigned ?
															<span className='text-sm italic'>E-signed by {noteData?.signed_by_name} on {moment(noteData?.signed_on).format(constants.dateformat)}</span> :
															<span></span>
													}
												</>
											)
										}
								</Space>
							</Form.Item>
						</Form>
					</Card>
				</Spin>
				</div>
			</div>
		</div>
	);
};

export default RecoveryPlan;